import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { checkoutIntentsClient } from 'app/checkout-intents/client';

type Money = {
  amountSubunits: number;
  currencyCode: string;
};

export type TransactionItem = {
  id: string;
  amount: Money;
  description?: string;
  metadata?: Record<string, string>;
  createdAt: string;
};

type TransactionsResponse = {
  data: TransactionItem[];
  pageInfo: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    endCursor?: string;
    startCursor?: string;
  };
};

const PAGE_SIZE = 20;

const fetchTransactions = async (after?: string): Promise<TransactionsResponse> => {
  const client = checkoutIntentsClient.get();
  if (!client) {
    throw new Error('API client not initialized');
  }

  const params = new URLSearchParams({ limit: String(PAGE_SIZE) });
  if (after) {
    params.set('after', after);
  }

  return client.get<TransactionsResponse>(`/api/v1/billing/transactions?${params}`);
};

const MAX_EXPORT_ROWS = 10_000;

export const fetchAllTransactions = async (): Promise<TransactionItem[]> => {
  const all: TransactionItem[] = [];
  let cursor: string | undefined;

  do {
    const page = await fetchTransactions(cursor);
    all.push(...page.data);
    if (all.length >= MAX_EXPORT_ROWS) {
      break;
    }
    cursor = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : undefined;
  } while (cursor);

  return all.slice(0, MAX_EXPORT_ROWS);
};

export const useDrawdownTransactions = () => {
  const [cursors, setCursors] = useState<string[]>([]);
  const currentCursor = cursors.at(-1);

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['drawdown-transactions', currentCursor],
    queryFn: () => fetchTransactions(currentCursor),
    placeholderData: (prev) => prev,
  });

  const nextPage = () => {
    const endCursor = data?.pageInfo.endCursor;
    if (endCursor) {
      setCursors((prev) => [...prev, endCursor]);
    }
  };

  const previousPage = () => {
    setCursors((prev) => prev.slice(0, -1));
  };

  return {
    transactions: data?.data ?? [],
    hasNextPage: data?.pageInfo.hasNextPage ?? false,
    hasPreviousPage: cursors.length > 0,
    loading,
    error,
    nextPage,
    previousPage,
    pageIndex: cursors.length,
  };
};
